import React, { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { EnterpriseSidebar } from './EnterpriseSidebar';
import { EnterpriseHeader } from './EnterpriseHeader';
import { EnterpriseCommandPalette } from '../enterprise/EnterpriseCommandPalette';
import { EnterpriseBreadcrumbs } from '../enterprise/EnterpriseBreadcrumbs';
import { ENTERPRISE_ADMIN_BASE } from '../../config/adminLayers';

function getEnterprisePageTitle(pathname: string): string {
  const base = ENTERPRISE_ADMIN_BASE;
  if (pathname === base || pathname === `${base}/`) {
    return 'Command overview';
  }
  if (!pathname.startsWith(`${base}/`)) {
    return 'Enterprise Admin';
  }
  const rest = pathname.slice(base.length + 1);

  if (rest === 'workspaces/table') return 'Workspaces (classic table)';
  if (rest === 'workspaces') return 'Workspace directory';
  if (rest.startsWith('workspaces/')) return 'Workspace overview';
  if (rest === 'verified-domains') return 'Sending domains';
  if (rest === 'audit') return 'Audit log';
  if (rest === 'settings') return 'Settings';
  if (rest === 'settings/invoice-templates') return 'Invoice templates';

  const parts = rest.split('/').filter(Boolean);
  if (parts.length >= 2 && parts[0] === 'users') return 'User detail';

  const SEGMENT: Record<string, string> = {
    dashboard: 'Ops dashboard',
    users: 'Users',
    pricing: 'Pricing',
    invoices: 'Invoices',
    'phone-numbers': 'Phone numbers',
    'calling-providers': 'Calling providers',
    'api-keys': 'API keys',
    workflows: 'Workflows',
    'workflow-health': 'Workflow health',
    integrations: 'Integrations',
    settings: 'Settings',
    system: 'System',
    monitoring: 'Monitoring',
    'fastapi-monitoring': 'FastAPI monitoring',
    'copilot-metrics': 'Copilot metrics',
    'supabase-monitor': 'Supabase monitor',
  };
  return SEGMENT[parts[0]] ?? 'Enterprise Admin';
}

/**
 * Enterprise admin shell — new layout for `/admin/app/*`.
 * Classic routes remain on ClassicAdminLayout; mirrored pages reuse the same components here.
 */
export const EnterpriseAdminLayout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [commandOpen, setCommandOpen] = useState(false);
  const location = useLocation();
  const title = getEnterprisePageTitle(location.pathname);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setCommandOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className="min-h-screen bg-enterprise-50">
      <EnterpriseCommandPalette open={commandOpen} onClose={() => setCommandOpen(false)} />
      <EnterpriseSidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

      <div className="lg:pl-72">
        <EnterpriseHeader
          setSidebarOpen={setSidebarOpen}
          title={title}
          onOpenCommandPalette={() => setCommandOpen(true)}
        />
        <main className="px-4 py-6 sm:px-6 lg:px-8">
          <EnterpriseBreadcrumbs />
          <Outlet />
        </main>
      </div>
    </div>
  );
};
